/** Thin wrapper around window.Telegram.WebApp (loaded in index.html). */

interface LocationData {
  latitude: number;
  longitude: number;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { id: number; first_name?: string } };
  platform: string;
  version: string;
  colorScheme: 'light' | 'dark';
  ready(): void;
  expand(): void;
  isVersionAtLeast?(v: string): boolean;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void;
    selectionChanged(): void;
    notificationOccurred(t: 'success' | 'error' | 'warning'): void;
  };
  BackButton?: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  requestContact?(cb: (shared: boolean, res?: { responseUnsafe?: { contact?: { phone_number?: string } } }) => void): void;
  LocationManager?: {
    isInited: boolean;
    isLocationAvailable: boolean;
    init(cb?: () => void): void;
    getLocation(cb: (data: LocationData | null) => void): void;
  };
  openTelegramLink?(url: string): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  setBottomBarColor?(color: string): void;
  showConfirm?(message: string, cb: (ok: boolean) => void): void;
  onEvent?(event: string, cb: (...args: unknown[]) => void): void;
  offEvent?(event: string, cb: (...args: unknown[]) => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/** Signed init data. In local development outside Telegram, VITE_DEV_INIT_DATA can be used. */
export function getInitData(): string {
  return tg?.initData || import.meta.env.VITE_DEV_INIT_DATA || '';
}

/** Agent code from an agent link: ?agent=AG-1234 (bot button) or start_param ag_1234 (direct link). */
export function getAgentHint(): { agent?: string; startParam?: string } {
  const agent = new URLSearchParams(window.location.search).get('agent') ?? undefined;
  return { agent, startParam: tg?.initDataUnsafe.start_param };
}

export function isIOS(): boolean {
  return tg?.platform ? ['ios', 'macos'].includes(tg.platform) : /iPhone|iPad|Mac/.test(navigator.userAgent);
}

export function haptic(kind: 'tap' | 'select' | 'success' | 'error' = 'tap') {
  const h = tg?.HapticFeedback;
  if (!h) return;
  if (kind === 'select') h.selectionChanged();
  else if (kind === 'success' || kind === 'error') h.notificationOccurred(kind);
  else h.impactOccurred('light');
}

/** Harmony's own colours (from the logo). Telegram's theme colours are not used: some themes made text too light. */
export const THEME = {
  light: { bg: '#FFFFFF', secondary: '#F3F2F7' },
  dark: { bg: '#13122A', secondary: '#1F1D3D' },
};

export function colorScheme(): 'light' | 'dark' {
  if (tg?.colorScheme) return tg.colorScheme;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Applies light or dark Harmony colours to the page and to Telegram's own header and background. */
export function applyTheme() {
  const scheme = colorScheme();
  document.documentElement.dataset.theme = scheme;
  const c = THEME[scheme];
  try {
    tg?.setHeaderColor?.(c.bg);
    tg?.setBackgroundColor?.(c.bg);
    tg?.setBottomBarColor?.(c.bg);
  } catch {
    // Older Telegram versions do not support these; the page colours still apply.
  }
  window.dispatchEvent(new CustomEvent('harmony:theme', { detail: scheme }));
}

export function initTelegram() {
  tg?.ready();
  tg?.expand();
  applyTheme();
  tg?.onEvent?.('themeChanged', applyTheme);
}

/** Asks Telegram for the user's phone number. Resolves null if declined or unsupported. */
export function requestPhone(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!tg?.requestContact) return resolve(null);
    let done = false;
    // Some clients deliver the number only through the contactRequested event.
    const onEvent = (e: unknown) => {
      const ev = e as { status?: string; responseUnsafe?: { contact?: { phone_number?: string } } };
      finish(ev?.status === 'sent' ? ev.responseUnsafe?.contact?.phone_number ?? null : null);
    };
    const finish = (v: string | null) => {
      if (done) return;
      done = true;
      tg?.offEvent?.('contactRequested', onEvent);
      resolve(v);
    };
    tg.onEvent?.('contactRequested', onEvent);
    try {
      tg.requestContact((shared, res) => {
        if (!shared) return finish(null);
        const phone = res?.responseUnsafe?.contact?.phone_number;
        if (phone) finish(phone);
      });
    } catch {
      finish(null);
    }
    setTimeout(() => finish(null), 60_000);
  });
}

/** Current position from Telegram's location manager, falling back to the browser. */
export function requestLocation(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    const browser = () => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, timeout: 15_000, maximumAge: 60_000 },
      );
    };
    const lm = tg?.LocationManager;
    if (!lm) return browser();
    const get = () => {
      if (!lm.isLocationAvailable) return browser();
      lm.getLocation((d) => resolve(d ? { lat: d.latitude, lng: d.longitude } : null));
    };
    if (lm.isInited) get();
    else lm.init(get);
  });
}

/** Native Telegram yes/no dialog, with the browser's as a fallback. */
export function confirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (tg?.showConfirm) return tg.showConfirm(message, resolve);
    } catch {
      // older Telegram versions
    }
    resolve(window.confirm(message));
  });
}
